import {SEGMENT} from "@reva/shared/reva-spec";

import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {ArchiveDocumentType, FinalizeValuationResponse} from "../payload-types/persistence-finalize";
import type {ValueIndicationRequest, ValueIndicationResponse} from "../payload-types/value-indication";
import {ValidationErrorI18n} from "../validation";

export class PreFinalizeApiInterface extends SimpleApiInterface<FinalizeValuationResponse> {
    private readonly revaId: string;
    private readonly segment: string | null;
    private readonly parametersInReva: object | null;
    private readonly valuationParamsOut: ValueIndicationResponse | null;
    private readonly valuationParamsIn: ValueIndicationRequest | null;
    private readonly parametersInRegister: object | null;
    private readonly parametersInEnergyCertificate: object | null;
    private readonly parametersInIdpEnergyCertificate: object | null;
    private readonly parametersInPlot: object | null;
    private readonly parametersInInspectionDerived: object | null;
    private readonly documentProperties: object | null;
    private readonly comment: string | null;
    private readonly documentTypes: ArchiveDocumentType[];

    constructor(
        revaId: string,
        segment: string | null,
        parametersInReva: object | null,
        valuationParamsOut: ValueIndicationResponse | null,
        valuationParamsIn: ValueIndicationRequest | null,
        parametersInRegister: object | null,
        parametersInEnergyCertificate: object | null,
        parametersInIdpEnergyCertificate: object | null,
        parametersInPlot: object | null,
        parametersInInspectionDerived: object | null,
        documentProperties: object | null,
        comment: string | null,
        documentTypes: ArchiveDocumentType[],
    ) {
        super();
        this.revaId = revaId;
        this.segment = segment;
        this.parametersInReva = parametersInReva;
        this.valuationParamsOut = valuationParamsOut;
        this.valuationParamsIn = valuationParamsIn;
        this.parametersInRegister = parametersInRegister;
        this.parametersInEnergyCertificate = parametersInEnergyCertificate;
        this.parametersInIdpEnergyCertificate = parametersInIdpEnergyCertificate;
        this.parametersInPlot = parametersInPlot;
        this.parametersInInspectionDerived = parametersInInspectionDerived;
        this.documentProperties = documentProperties;
        this.comment = comment;
        this.documentTypes = documentTypes;
    }

    protected validate(): void {
        if (!this.valuationParamsOut) {
            throw new ValidationErrorI18n("valuationParamsOut", "object.error.finalizationMissingValuation");
        }
        if (!this.valuationParamsIn) {
            throw new ValidationErrorI18n("valuationParamsIn", "object.error.finalizationMissingValuation");
        }
        // Für alle Segmente (außer GRST_K) werden die EnergyValue-Details (unter `energy_value_details`)
        // für die vorläufige Finalisierung benötigt (und serverseitig in REVA-REST validiert).
        // Grundstücke haben kein Gebäude und damit regulär keine Energiekennwert-Indikation.
        if (this.segment === SEGMENT.GRST_K) {
            return;
        }
        if (!this.valuationParamsOut.energy_value_details) {
            throw new ValidationErrorI18n("energy_value_details", "object.error.finalizationMissingEnergy");
        }
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_VALUATIONS, this.revaId, RevaApi.PATH_VALUATION_PRE_FINALIZE]);
    }

    protected buildPayload(): object {
        return {
            parametersInReva: this.parametersInReva,
            valuationParamsOut: this.valuationParamsOut,
            valuationParamsIn: this.valuationParamsIn,
            parametersInRegister: this.parametersInRegister,
            parametersInEnergyCertificate: this.parametersInEnergyCertificate,
            parametersInIdpEnergyCertificate: this.parametersInIdpEnergyCertificate,
            parametersInPlot: this.parametersInPlot,
            parametersInInspectionDerived: this.parametersInInspectionDerived,
            documentProperties: this.documentProperties,
            comment: this.comment,
            documentTypes: this.documentTypes,
        };
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<FinalizeValuationResponse> {
        return executor.fetchPOST(url, credentials, this.buildPayload());
    }
}
